import {
	Event,
	EventsApiResponse,
	eventsApiResponseSchema,
	eventSchema,
	TasksApiResponse,
	taskListSchema,
	LogApiResponse,
	logApiResponseSchema,
	Document,
	documentListSchema,
	DocumentsApiResponse,
	rolesListResponseSchema,
	UsersApiResponse,
	usersApiResponseSchema,
	Form,
	Role,
	formSchema,
} from '@repo/shared/domain';
import { constructQuery } from '@repo/shared/helpers';
import { guardedClient as clientRequest } from '@repo/shared/helpers';

export const getEvents = async (query: string): Promise<EventsApiResponse> => {
	const { body } = await clientRequest.get(`/api/events?${query}`, {
		schema: eventsApiResponseSchema,
	});
	return body;
};

export const getEvent = async (eventId: string, query?: string): Promise<Event | undefined> => {
	try {
		const { body } = await clientRequest.get(`/api/events/${eventId}${query ? `?${query}` : ''}`, {
			schema: eventSchema,
		});
		return body;
	} catch (error) {
		console.error('Failed to get event:', error);
		return undefined;
	}
};

export const getEventUsers = async (
	eventId: string,
	params?: { page?: number; sort?: string; filter?: string },
): Promise<UsersApiResponse> => {
	const query = constructQuery({
		page: params?.page,
		sort: params?.sort,
		filter: params?.filter,
		eventId,
	});
	const { body } = await clientRequest.get(`/api/events/${eventId}/users?${query}`, {
		schema: usersApiResponseSchema,
	});
	return body;
};

export const getEventTasks = async (
	eventId: string,
	params?: { page?: number; sort?: string },
): Promise<TasksApiResponse> => {
	const query = constructQuery({
		page: params?.page,
		sort: params?.sort,
		eventId,
	});
	const { body } = await clientRequest.get(`/api/tasks?${query}`, {
		schema: taskListSchema,
	});
	return body;
};

export const getEventApprovals = async (
	eventId: string,
	params?: { page?: number; sort?: string },
): Promise<TasksApiResponse> => {
	const query = constructQuery({
		page: params?.page,
		sort: params?.sort,
		eventId,
	});
	const { body } = await clientRequest.get(`/api/tasks/approvals/view-only?${query}`, {
		schema: taskListSchema,
	});
	return body;
};

export const getEventDocuments = async (
	eventId: string,
	params?: { filter?: string; page?: number },
): Promise<DocumentsApiResponse> => {
	const query = constructQuery({
		page: params?.page,
		all: params?.filter === 'all' ? 'yes' : '',
	});
	const { body } = await clientRequest.get(`/api/events/${eventId}/files?${query}`, {
		schema: documentListSchema,
	});
	return body;
};

export const getEventDocument = async (eventId: string, documentId: string): Promise<Document | null> => {
	try {
		const query = constructQuery({
			all: 'yes',
			includeLogs: 'true',
			fileIds: documentId,
		});
		const { body } = await clientRequest.get(`/api/events/${eventId}/files?${query}`, {
			schema: documentListSchema,
		});
		return body?.data?.[0] ?? null;
	} catch (error) {
		console.error('Failed to get event document:', error);
		return null;
	}
};

export const getEventLogs = async (
	eventId: string,
	params?: { from?: string; until?: string; page?: number; sort?: string },
): Promise<LogApiResponse> => {
	const query = constructQuery({
		type: 'EventLog',
		eventId,
		from: params?.from,
		until: params?.until,
		page: params?.page,
		sort: params?.sort || 'timestamp:desc',
	});
	const { body } = await clientRequest.get(`/api/logs/type?${query}`, {
		schema: logApiResponseSchema,
	});
	return body;
};

export const getEventsWithHierarchy = async (query: string): Promise<EventsApiResponse> => {
	const { body } = await clientRequest.get(`/api/events/hierarchy?${query}`, {
		schema: eventsApiResponseSchema,
	});
	return body;
};

export const getEventRoles = async (eventId: string): Promise<Role[]> => {
	const { body } = await clientRequest.get(`/api/events/${eventId}/roles`, {
		schema: rolesListResponseSchema,
	});
	return body;
};

export const getEventForm = async (formId: string, language?: string): Promise<Form | undefined> => {
	try {
		const query = constructQuery({ language });
		const { body } = await clientRequest.get(`/api/forms/${formId}${query ? `?${query}` : ''}`, {
			schema: formSchema,
		});
		return body;
	} catch (error) {
		console.error('Failed to get event form:', error);
		return undefined;
	}
};

export const getUserEvents = async (
	userId: string,
	params?: { page?: number; sort?: string },
): Promise<EventsApiResponse> => {
	const query = constructQuery({
		page: params?.page,
		sort: params?.sort,
		userIds: userId,
	});
	const { body } = await clientRequest.get(`/api/events?${query}`, {
		schema: eventsApiResponseSchema,
	});
	return body;
};

export const getGroupEvents = async (
	groupId: string,
	params?: { page?: number; sort?: string },
): Promise<EventsApiResponse> => {
	const query = constructQuery({
		page: params?.page,
		sort: params?.sort,
		groupIds: groupId,
	});
	const { body } = await clientRequest.get(`/api/events?${query}`, {
		schema: eventsApiResponseSchema,
	});
	return body;
};
